import AntIcon from "@/components/icons/AntIcon";
import { Button, Descriptions, Flex } from "antd";
import { useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { Permission } from "../../../common/security/enums";
import { AgentStatus } from "../../agent/enums";
import { formatAgentName } from "../../agent/utils";
import { useCurrentAgentAccount, useHasPermissions, useIsCurrentAgentTypePlus } from "../../auth/hooks";
import { ContractOrigin, ContractVerificationStatus } from "../enums";
import { Contract } from "../types";
import { contractPermissionsByContractType } from "../utils";
import { ContractGainerRecords } from "./contract-gainer-records/ContractGainerRecords";
import { ContractPredictedCommissions } from "./ContractPredictedCommissions";
import { ContractUpdateGainersModal } from "./ContractUpdateGainersModal";

type Props = {
  contract: Contract;
};

export const ContractAgentsData = ({ contract }: Props) => {
  const { t } = useTranslation();

  const [showGainerChangeModal, setShowGainerChangeModal] = useState(false);

  const isTopAgentPlus = useIsCurrentAgentTypePlus();
  const currentAgentUserAccount = useCurrentAgentAccount();
  const hasCommissionsPermission = useHasPermissions(Permission.COMMISSIONS);
  const hasUpdateContractPermission = useHasPermissions(contractPermissionsByContractType[contract.type].update);
  const hasReadContractPermission = useHasPermissions(contractPermissionsByContractType[contract.type].read);

  const showUpdateGainers = useMemo(() => {
    const origin =
      contract.origin === ContractOrigin.NN ||
      contract.origin === ContractOrigin.EUCS ||
      contract.origin === ContractOrigin.LUNA;

    const verificationStatus = contract.verificationStatus === ContractVerificationStatus.TYPED;

    const agentPermissions =
      hasReadContractPermission &&
      currentAgentUserAccount?.agent.status === AgentStatus.BROKER_WITH_CONTRACT &&
      (contract.signer?.id === currentAgentUserAccount?.agent.id ||
        currentAgentUserAccount.representingAgent?.id === contract.signer?.id);

    return isTopAgentPlus && origin && verificationStatus && (hasUpdateContractPermission || agentPermissions);
  }, [contract, currentAgentUserAccount, hasUpdateContractPermission, hasReadContractPermission, isTopAgentPlus]);

  const showPredictedCommissions =
    contract.predictedCommission?.canBeCalculated &&
    (hasCommissionsPermission || contract.predictedCommission.calculatedWithoutErrors);

  return (
    <Descriptions
      size="small"
      bordered
      column={1}
      styles={{ label: { width: 220 } }}
      items={[
        { label: t("contract.attrs.signer"), children: formatAgentName(contract.signer) },
        { label: t("contract.attrs.manager"), children: formatAgentName(contract.manager) },
        {
          label: t("contract.attrs.gainerRecords._label"),
          children: (
            <Flex
              vertical={(contract.gainerRecords ?? [])?.length > 1}
              justify={(contract.gainerRecords ?? [])?.length > 1 ? undefined : "space-between"}
              gap={16}
              style={(contract.gainerRecords ?? [])?.length > 1 ? { width: "100%" } : { flexDirection: "row-reverse" }}
            >
              {showUpdateGainers && (
                <Flex justify="end">
                  <Button
                    size="small"
                    type="primary"
                    icon={<AntIcon type="edit" />}
                    onClick={() => setShowGainerChangeModal(true)}
                  >
                    {t("contract.actions.updateGainers")}
                  </Button>

                  <ContractUpdateGainersModal
                    contract={contract}
                    isOpen={showGainerChangeModal}
                    onCancel={() => setShowGainerChangeModal(false)}
                  />
                </Flex>
              )}

              <Flex flex="auto" vertical justify="center" style={{ width: "100%" }}>
                <ContractGainerRecords gainerRecords={contract.gainerRecords} />
              </Flex>
            </Flex>
          ),
        },
        { label: t("contract.attrs.affiliatePartner"), children: contract.affiliatePartner?.name },
        ...(showPredictedCommissions
          ? [
              {
                label: t("contract.attrs.predictedCommissions._label"),
                children: <ContractPredictedCommissions predictedCommission={contract.predictedCommission} />,
              },
            ]
          : []),
      ]}
    />
  );
};
